import { type ButtonHTMLAttributes, type Ref, useMemo } from 'react';
import { sanitizeCSSVariables } from '../../../util/style';
import { buildTypographyStyleValue, Typography, type TypographyProps } from '../../typography';
import { useMergedTypography } from '../../typography/context';
import type { BaseLinkUnderlineCondition } from '../base-link';
import { buildTextLinkClassName } from './utils';

export type TextButtonSize = 'xsmall' | 'small' | 'medium' | 'large';

const typographyPropsBySize: Record<TextButtonSize, TypographyProps> = {
  xsmall: {
    wrap: 'no-wrap',
    size: '0.75rem',
    lineHeight: '1rem',
    weight: 'medium',
    decoration: 'inherit',
    tone: 'md',
  },
  small: {
    wrap: 'no-wrap',
    size: '0.875rem',
    lineHeight: '1.5rem',
    weight: 'medium',
    decoration: 'inherit',
    tone: 'md',
  },
  medium: {
    wrap: 'no-wrap',
    size: '1rem',
    lineHeight: '1.5rem',
    weight: 'medium',
    decoration: 'inherit',
    tone: 'md',
  },
  large: {
    wrap: 'no-wrap',
    size: '1.125rem',
    lineHeight: '1.625rem',
    weight: 'medium',
    decoration: 'inherit',
    tone: 'md',
  },
};

interface TextButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  fullWidth?: boolean;
  ref?: Ref<HTMLButtonElement>;
  size?: TextButtonSize;
  underline?: BaseLinkUnderlineCondition;
  typography?: TypographyProps;
}

export function TextButton({
  className,
  children,
  fullWidth,
  size = 'medium',
  typography: providedTypography,
  underline = 'always',
  ref,
  style: providedStyle,
  ...rest
}: TextButtonProps) {
  const typography = useMergedTypography({ ...typographyPropsBySize[size], ...providedTypography } as TypographyProps, 'current');
  const style = useMemo(
    () => sanitizeCSSVariables(buildTypographyStyleValue(typography.tone, providedStyle, '--ixf-text-link-color')),
    [typography, providedStyle],
  );

  return (
    <button className={buildTextLinkClassName({ className, fullWidth, underline })} ref={ref} style={style} type="button" {...rest}>
      <Typography inheritance="current" {...typography}>
        {children}
      </Typography>
    </button>
  );
}
